import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaHandshake, FaTrash } from 'react-icons/fa';
import {
  TbClick,
  TbEye,
  TbExternalLink,
  TbPlayerPause,
  TbPlayerPlay,
  TbPlus,
} from 'react-icons/tb';
import { twMerge } from 'tailwind-merge';
import PrimaryButton from '../buttons/PrimaryButton';
import ApiImage from './ApiImage';
import AreYouSureModal from '../modals/AreYouSureModal';
import AddEditSponsorModal from '../modals/AddEditSponsorModal';
import PageHeader from './PageHeader';
import EmptyScreenView from './EmptyScreenView';
import SponsorCampaignTypeBadge from './SponsorCampaignTypeBadge';
import SponsorStatusBadge from './SponsorStatusBadge';
import { ActionMenu, ActionMenuButton } from '../menus/ActionMenu';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../tables/BaseTable';
import sponsorService from '../../../services/business/sponsorService';
import Sponsor from '../../../types/sponsor';

function formatWebsite(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function SponsorRow({
  sponsor,
  onView,
  onDelete,
  onToggleActive,
  toggling,
  t,
}: {
  sponsor: Sponsor;
  onView: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  toggling: boolean;
  t: (key: string) => string;
}) {
  return (
    <BaseTableDataRow
      onClick={onView}
      className={twMerge(
        'group cursor-pointer transition-colors hover:bg-secondary-50',
        !sponsor.active && 'opacity-60',
      )}>
      <td className="py-3.5">
        <div className="flex items-center gap-3">
          {sponsor.logo ? (
            <ApiImage
              path={sponsor.logo}
              alt={sponsor.name}
              className="h-10 w-auto max-w-[7.5rem] flex-shrink-0 rounded-lg bg-secondary-50 object-contain"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-secondary-200">
              <FaHandshake className="text-primary-700 h-5 w-5" />
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate font-semibold text-gray-900">{sponsor.name}</span>
              <SponsorCampaignTypeBadge type={sponsor.campaignType} />
              <SponsorStatusBadge active={sponsor.active} />
            </div>
            {sponsor.description && (
              <span className="line-clamp-1 text-sm text-gray-500">{sponsor.description}</span>
            )}
          </div>
        </div>
      </td>
      <td className="hidden py-3.5 md:table-cell">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span
            className="inline-flex items-center gap-1"
            title={t('page.sponsors.details.stats.impressions')}>
            <TbEye className="h-4 w-4" />
            {sponsor.impressionCount}
          </span>
          <span
            className="inline-flex items-center gap-1"
            title={t('page.sponsors.details.stats.clicks')}>
            <TbClick className="h-4 w-4" />
            {sponsor.clickCount}
          </span>
          <span
            className="inline-flex items-center gap-1"
            title={t('page.sponsors.details.stats.websiteOpens')}>
            <TbExternalLink className="h-4 w-4" />
            {sponsor.websiteOpenCount ?? 0}
          </span>
        </div>
      </td>
      <td className="py-3.5">
        {sponsor.url ? (
          <a
            href={sponsor.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-primary-700 inline-block max-w-xs truncate text-sm font-medium hover:underline">
            {formatWebsite(sponsor.url)}
          </a>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="py-3.5 pr-4">
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <ActionMenu>
            <ActionMenuButton icon={FaEye} text={t('page.sponsors.view')} onClick={onView} />
            <ActionMenuButton
              icon={sponsor.active ? TbPlayerPause : TbPlayerPlay}
              text={t(sponsor.active ? 'page.sponsors.deactivate' : 'page.sponsors.activate')}
              onClick={onToggleActive}
              disabled={toggling}
            />
            <ActionMenuButton
              icon={FaTrash}
              text={t('general.delete')}
              className="text-danger"
              onClick={onDelete}
            />
          </ActionMenu>
        </div>
      </td>
    </BaseTableDataRow>
  );
}

export default function SponsorsSettings(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingSponsor, setDeletingSponsor] = useState<Sponsor | undefined>(undefined);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const cancelledRef = useRef(false);

  const fetchSponsors = useCallback(() => {
    cancelledRef.current = false;
    setLoading(true);
    setLoadError(false);
    sponsorService
      .getSponsors()
      .then((data) => {
        if (!cancelledRef.current) {
          setSponsors(data);
          setLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelledRef.current) {
          setSponsors([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchSponsors();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSponsors]);

  const totals = useMemo(() => {
    return sponsors.reduce(
      (acc, s) => {
        acc.impressions += s.impressionCount ?? 0;
        acc.clicks += s.clickCount ?? 0;
        acc.websiteOpens += s.websiteOpenCount ?? 0;
        if (s.active) acc.active += 1;
        if (s.campaignType === 'FEATURE') acc.feature += 1;
        else acc.community += 1;
        return acc;
      },
      { impressions: 0, clicks: 0, websiteOpens: 0, community: 0, feature: 0, active: 0 },
    );
  }, [sponsors]);

  const handleAdd = (): void => {
    setModalOpen(true);
  };

  const handleView = (sponsor: Sponsor): void => {
    navigate(`/sponsors/${sponsor.sponsorId}`);
  };

  const handleSaved = (saved: Sponsor): void => {
    navigate(`/sponsors/${saved.sponsorId}`);
  };

  const handleToggleActive = async (sponsor: Sponsor): Promise<void> => {
    setTogglingId(sponsor.sponsorId);
    try {
      const updated = await sponsorService.updateSponsor(sponsor.sponsorId, {
        active: !sponsor.active,
      });
      setSponsors((prev) =>
        prev.map((s) => (s.sponsorId === sponsor.sponsorId ? { ...s, ...updated } : s)),
      );
      toast.success(t(updated.active ? 'page.sponsors.activated' : 'page.sponsors.deactivated'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deletingSponsor) return;
    const { sponsorId } = deletingSponsor;
    try {
      await sponsorService.deleteSponsor(sponsorId);
      setSponsors((prev) => prev.filter((s) => s.sponsorId !== sponsorId));
      toast.success(t('component.modal.sponsor.deleted'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setDeletingSponsor(undefined);
    }
  };

  const addButton = (): JSX.Element => (
    <PrimaryButton text={t('page.sponsors.add')} icon={TbPlus} onClick={handleAdd} />
  );

  return (
    <>
      <PageHeader
        title={t('page.sponsors.title')}
        subtitle={t('page.sponsors.subtitle')}
        primaryButton={addButton()}
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">{t('general.loading')}</p>
        </div>
      ) : loadError ? (
        <EmptyScreenView
          title={t('toast.error.unexpected')}
          subtitle={t('page.sponsors.loadError', {
            defaultValue: t('toast.error.unexpected'),
          })}
          primaryButton={
            <PrimaryButton text={t('general.retry')} onClick={() => fetchSponsors()} />
          }
        />
      ) : sponsors.length === 0 ? (
        <EmptyScreenView
          title={t('page.sponsors.emptyTitle')}
          subtitle={t('page.sponsors.emptyText')}
          primaryButton={addButton()}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <div className="rounded-[12px] bg-secondary-200/70 p-4">
              <p className="text-sm text-primary-900/70">{t('page.sponsors.stats.campaigns')}</p>
              <p className="mt-1 text-2xl font-bold text-primary-900">{sponsors.length}</p>
            </div>
            <div className="rounded-[12px] bg-secondary-200/70 p-4">
              <p className="text-sm text-primary-900/70">{t('page.sponsors.stats.active')}</p>
              <p className="mt-1 text-2xl font-bold text-primary-900">{totals.active}</p>
            </div>
            <div className="rounded-[12px] bg-secondary-200/70 p-4">
              <p className="text-sm text-primary-900/70">{t('page.sponsors.stats.impressions')}</p>
              <p className="mt-1 text-2xl font-bold text-primary-900">{totals.impressions}</p>
            </div>
            <div className="rounded-[12px] bg-secondary-200/70 p-4">
              <p className="text-sm text-primary-900/70">{t('page.sponsors.stats.clicks')}</p>
              <p className="mt-1 text-2xl font-bold text-primary-900">{totals.clicks}</p>
            </div>
            <div className="rounded-[12px] bg-secondary-200/70 p-4">
              <p className="text-sm text-primary-900/70">{t('page.sponsors.stats.websiteOpens')}</p>
              <p className="mt-1 text-2xl font-bold text-primary-900">{totals.websiteOpens}</p>
            </div>
          </div>

          <BaseTable>
            <BaseTableHeader>
              <BaseTableHeaderRow>
                <th className="text-left">{t('page.sponsors.columns.campaign')}</th>
                <th className="hidden text-left md:table-cell">
                  {t('page.sponsors.columns.performance')}
                </th>
                <th className="text-left">{t('component.modal.sponsor.url')}</th>
                <th className="text-right" />
              </BaseTableHeaderRow>
            </BaseTableHeader>
            <BaseTableBody>
              <BaseTableSpacerRow />
              {sponsors.map((sponsor) => (
                <SponsorRow
                  key={sponsor.sponsorId}
                  sponsor={sponsor}
                  onView={() => handleView(sponsor)}
                  onDelete={() => setDeletingSponsor(sponsor)}
                  onToggleActive={() => handleToggleActive(sponsor)}
                  toggling={togglingId === sponsor.sponsorId}
                  t={t}
                />
              ))}
            </BaseTableBody>
          </BaseTable>
        </div>
      )}

      <AddEditSponsorModal open={modalOpen} setOpen={setModalOpen} onSaved={handleSaved} />

      <AreYouSureModal
        open={!!deletingSponsor}
        setOpen={(open) => {
          if (!open) setDeletingSponsor(undefined);
        }}
        title={t('component.modal.sponsor.deleteTitle')}
        description={t('component.modal.sponsor.deleteDescription', {
          name: deletingSponsor?.name,
        })}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
